import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import TryItButton from "../../../TryItButton";
import OutputBox from "../../../OutputBox";

export default function JS_Object_Display() {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">🧩 JavaScript Object Display</h1>

      <p className="tutorial-intro">
        In JavaScript, objects can be displayed or represented in various ways.
        This includes using{" "}
        <code className="highlight_code">console.log()</code>,
        <code className="highlight_code">JSON.stringify()</code>, or directly
        accessing object properties.
      </p>

      {/* 🧠 Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem 2rem",
          borderRadius: "12px",
          margin: "2rem 0",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🧠 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            <code className="highlight_code">console.log(obj)</code> displays
            the object in the console (expandable in DevTools).
          </li>
          <li>
            <code className="highlight_code">JSON.stringify(obj)</code> converts
            the object into a readable string.
          </li>
          <li>Directly accessing properties shows individual values.</li>
          <li>
            Loops like <code className="highlight_code">for...in</code> or{" "}
            <code className="highlight_code">Object.keys()</code> can iterate
            through object contents.
          </li>
        </ul>
      </div>

      {/* 1️⃣ Basic Example */}
      <div ref={null} className="tutorial-section">
        <h3>1️⃣ Displaying Objects</h3>
        <p>Let's display an object using different techniques in JavaScript.</p>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`const person = { name: "Alice", age: 25, city: "Paris" };

// Display in console
console.log(person);

// Display as a string
console.log(JSON.stringify(person));

// Display specific property
console.log(person.name);`}
          />
          <TryItButton
            code={`const person = { name: "Alice", age: 25, city: "Paris" };
console.log(person);
console.log(JSON.stringify(person));
console.log(person.name);`}
          />
        </div>

        <OutputBox>
          Output: {"{ name: 'Alice', age: 25, city: 'Paris' }"} <br />
          {'{"name":"Alice","age":25,"city":"Paris"'} <br />
          Alice
        </OutputBox>
      </div>

      {/* 2️⃣ Using Loop */}
      <div className="tutorial-section">
        <h3>2️⃣ Display Using Loop</h3>
        <p>
          You can loop through an object's keys and values using{" "}
          <code className="highlight_code">for...in</code>.
        </p>

        <div className="code-demo-box read-only-box">
          <CodeCopyBlock
            code={`const car = { brand: "Toyota", model: "Camry", year: 2023 };

for (let key in car) {
  console.log(key + ": " + car[key]);
}`}
          />
          <TryItButton
            code={`const car = { brand: "Toyota", model: "Camry", year: 2023 };
for (let key in car) {
  console.log(key + ": " + car[key]);
}`}
          />
        </div>

        <OutputBox>
          Output: <br />
          brand: Toyota <br />
          model: Camry <br />
          year: 2023
        </OutputBox>
      </div>

      {/* 🚀 Summary */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem 2rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#075985" }}>🚀 Summary</h2>
        <ul className="tutorial-list-points">
          <li>
            Use <code className="highlight_code">console.log()</code> for
            debugging or development.
          </li>
          <li>
            Use <code className="highlight_code">JSON.stringify()</code> for
            displaying as a string.
          </li>
          <li>Access properties directly or loop through them as needed.</li>
        </ul>
      </div>
    </div>
  );
}
